/**
 * Guided-flow copy + estimates for the Stage-3 next-up pointer (s80).
 *
 * WHY_LINES: one-line "why this matters" per section, keyed by slug. The pointer
 * shows it under the section name. UNMAPPED sections render NO why-line — never a
 * placeholder (the map is the at-launch curated set). Sentence case, verbatim.
 *
 * `estimateMinutes`: a computed, never-authored time estimate from the active
 * field count in the section-overview payload.
 */
import { SECTION_SLUGS } from "@shared/constants/sections";

export const WHY_LINES: Record<string, string> = {
  [SECTION_SLUGS.LEGAL_POWERS_OF_ATTORNEY]: "Who can act for you if you can't — ends at death, vital before it.",
  [SECTION_SLUGS.INSTRUCTIONS_FOR_EXECUTORS_FUNERAL_WISHES]: "The first decisions your family faces — spare them the guessing.",
  // Keyed by the literal slug (not a SECTION_SLUGS constant): "beneficiaries" is a
  // common word that collides with non-slug literals, so promoting it to a constant
  // trips check:slugs (the s77 BLUEPRINT-IN-SPEC decision — see section-layout-config.ts).
  "beneficiaries": "Who gets what — probate needs this early.",
  [SECTION_SLUGS.LEGAL_IMPORTANT_DOCUMENTS]: "The one document everything else depends on.",
  [SECTION_SLUGS.LEGAL_PROFESSIONAL_CONTACTS]: "The lawyer and advisors your executor will call first.",
  [SECTION_SLUGS.PERSONAL_INFORMATION_HEALTH_MEDICAL]: "Your care wishes if you can't speak for yourself.",
  [SECTION_SLUGS.FINANCIAL_BANK_ACCOUNTS]: "Your executor must notify every institution — list them here.",
  [SECTION_SLUGS.FINANCIAL_AND_INSURANCE_INSURANCE_POLICIES]: "Often the family's first money in — make it findable.",
  [SECTION_SLUGS.CHILDREN_DEPENDENTS_GUARDIANSHIP]: "Who raises your children — the hardest, most important call.",
  [SECTION_SLUGS.FINANCIAL_INCOME_PENSIONS]: "What stops, what continues, what your family can claim.",
  [SECTION_SLUGS.FINANCIAL_DEBTS_LIABILITIES]: "What must be settled before anything is passed on.",
  [SECTION_SLUGS.FINANCIAL_INVESTMENTS_RRSP]: "Accounts your executor can't find are accounts that get lost.",
  [SECTION_SLUGS.FINANCIAL_TAXES]: "The estate can't close without the final return.",
  [SECTION_SLUGS.ASSETS_PROPERTIES]: "Homes need securing, insuring, and eventually transferring.",
  [SECTION_SLUGS.ASSETS_VEHICLE_LIST]: "Vehicles need insurance, storage, and transfer decisions fast.",
  [SECTION_SLUGS.ASSETS_VALUABLES]: "The things that matter — where they are and who should have them.",
  [SECTION_SLUGS.PERSONAL_INFORMATION_GOVERNMENT_IDS]: "Cancelling IDs stops fraud against the estate.",
  [SECTION_SLUGS.PASSWORD_LOG_DEVICES_LOCAL_ACCESS]: "A locked phone can lock away everything else.",
  [SECTION_SLUGS.PASSWORD_LOG_PRIMARY_EMAIL_IDENTITY_ACCOUNTS]: "Email is the master key to nearly every account.",
  [SECTION_SLUGS.FINANCIAL_AND_INSURANCE_PRE_AUTHORIZED_DEBITS]: "Subscriptions keep charging until someone stops them.",
  [SECTION_SLUGS.INSTRUCTIONS_FOR_EXECUTORS_LETTERS]: "Words they'll keep forever.",
  [SECTION_SLUGS.FAMILY_FRIENDS_AND_PETS_IMPORTANT_CONTACTS]: "The people who should hear the news from a person, not a bill.",
  [SECTION_SLUGS.FAMILY_PETS]: "Who takes them, what they need.",
  [SECTION_SLUGS.PERSONAL_INFORMATION_HEALTH_INSURANCE]: "Coverage to claim, coverage to cancel.",
};

/** The why-line for a section slug, or undefined when unmapped (render nothing). */
export function whyLineFor(sectionSlug: string | undefined): string | undefined {
  return sectionSlug ? WHY_LINES[sectionSlug] : undefined;
}

// s83: per-section completion-hint overrides for the muted "what's needed to
// complete" line under a section name (GenericCategoryPage guidanceText). The
// generic copy ("Fill in the starred (*) fields" / "Add at least one X") misleads
// when the starred field that actually satisfies the essential is DRAWER-only
// (invisible in the compact row) or the predicate needs a COUNT — the planner gets
// no clue what to do. These state the real requirement in brand voice (short, direct
// verbs). Only essential-bearing sections with that wrinkle are listed; every other
// section keeps the accurate generic hint.
export const COMPLETION_HINTS: Record<string, string> = {
  [SECTION_SLUGS.PERSONAL_INFORMATION_GOVERNMENT_IDS]: "Record the ID number for at least 2 of these",
  [SECTION_SLUGS.INSTRUCTIONS_FOR_EXECUTORS_FUNERAL_WISHES]: "Write your wishes for at least one topic",
  [SECTION_SLUGS.PASSWORD_LOG_DEVICES_LOCAL_ACCESS]: "Save how to unlock at least one device",
  [SECTION_SLUGS.PASSWORD_LOG_PRIMARY_EMAIL_IDENTITY_ACCOUNTS]: "Save the password for at least one account",
};

/** Predicate-accurate completion-hint override for a section, or undefined when the
 *  generic hint is already accurate. */
export function completionHintFor(sectionSlug: string | undefined): string | undefined {
  return sectionSlug ? COMPLETION_HINTS[sectionSlug] : undefined;
}

/**
 * Computed time estimate (minutes) from the active field count — half a minute a
 * field, floored at 2 and capped at 15. Display as "~N min".
 */
export function estimateMinutes(fieldCount: number | undefined): number | undefined {
  if (fieldCount == null) return undefined;
  return Math.max(2, Math.min(15, Math.ceil(fieldCount * 0.5)));
}
